import {useState} from 'react'
import {
  AlertTriangle,
  Bot,
  CheckCircle,
  ChevronDown,
  Edit2,
  Lock,
  Shield,
  Trash2,
  Users,
} from 'lucide-react'
import {
  getCategoryLabel,
  getPermissionCategory,
  getPermissionRisk,
  isAgentStudioPermission,
  type Permission,
  type PermissionId,
} from './permissionMetadata'

type PermissionCardProps = {
  group: {
    id_feature_group: PermissionId
    name: string
    data: PermissionId[]
  }
  permissions: Permission[]
  onEdit: (group: any) => void
  onDelete: (group: any) => void
  isExpanded?: boolean
  onToggleExpand?: () => void
  memberCount?: number
  searchTerm?: string
}

export const PermissionCard = ({
  group,
  permissions,
  onEdit,
  onDelete,
  isExpanded: externalIsExpanded,
  onToggleExpand,
  memberCount,
  searchTerm = '',
}: PermissionCardProps) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false)

  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pt-BR')

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand()
    } else {
      setInternalIsExpanded(!internalIsExpanded)
    }
  }

  const permissionMap = new Map(
    permissions.map((permission) => [permission.id_feature, permission])
  )
  const groupedPermissions = group.data.reduce((acc, permissionId) => {
    const permission = permissionMap.get(permissionId)
    if (!permission) return acc

    const category = getPermissionCategory(permission.name)
    if (!acc[category]) acc[category] = []
    acc[category].push(permission)
    return acc
  }, {} as Record<string, Permission[]>)

  const resolvedPermissions = Object.values(groupedPermissions).flat()
  const totalPermissions = group.data.length
  const categoryCount = Object.keys(groupedPermissions).length
  const agentStudioCount = resolvedPermissions.filter(isAgentStudioPermission).length
  const sensitiveCount = resolvedPermissions.filter(
    (permission) => getPermissionRisk(permission.name) !== 'standard'
  ).length
  const matchingPermissionIds = new Set(
    normalizedSearch
      ? resolvedPermissions
          .filter((permission) => {
            const searchableText = [
              permission.title,
              permission.name,
              getCategoryLabel(getPermissionCategory(permission.name)),
            ]
              .join(' ')
              .toLocaleLowerCase('pt-BR')
            return searchableText.includes(normalizedSearch)
          })
          .map((permission) => String(permission.id_feature))
      : []
  )

  return (
    <article className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md'>
      <div className='flex min-h-[68px] items-center gap-2 px-3 py-2.5 sm:px-4'>
        <button
          type='button'
          onClick={toggleExpand}
          aria-expanded={isExpanded}
          className='flex min-w-0 flex-1 items-center gap-3 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
        >
          <span className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600'>
            <Shield className='h-4 w-4' />
          </span>

          <span className='min-w-0 flex-1'>
            <span className='flex items-center gap-2'>
              <span className='truncate text-sm font-bold text-gray-900'>{group.name}</span>
              {matchingPermissionIds.size > 0 && (
                <span className='hidden rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700 sm:inline-flex'>
                  {matchingPermissionIds.size}{' '}
                  {matchingPermissionIds.size === 1 ? 'correspondência' : 'correspondências'}
                </span>
              )}
            </span>

            <span className='mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-gray-500'>
              <span className='inline-flex items-center gap-1'>
                <CheckCircle className='h-3.5 w-3.5 text-emerald-500' />
                {totalPermissions} {totalPermissions === 1 ? 'permissão' : 'permissões'}
              </span>
              <span className='inline-flex items-center gap-1'>
                <Lock className='h-3.5 w-3.5 text-violet-500' />
                {categoryCount} {categoryCount === 1 ? 'categoria' : 'categorias'}
              </span>
              {memberCount !== undefined && (
                <span className='inline-flex items-center gap-1'>
                  <Users className='h-3.5 w-3.5 text-gray-400' />
                  {memberCount} {memberCount === 1 ? 'usuário' : 'usuários'}
                </span>
              )}
              {agentStudioCount > 0 && (
                <span className='inline-flex items-center gap-1 text-blue-600'>
                  <Bot className='h-3.5 w-3.5' /> {agentStudioCount} Agent Studio
                </span>
              )}
              {sensitiveCount > 0 && (
                <span className='inline-flex items-center gap-1 text-amber-600'>
                  <AlertTriangle className='h-3.5 w-3.5' /> {sensitiveCount} sensíveis
                </span>
              )}
            </span>
          </span>
        </button>

        <div className='flex flex-shrink-0 items-center gap-0.5'>
          <button
            type='button'
            onClick={() => onEdit(group)}
            className='rounded-md p-2 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            title='Editar grupo'
            aria-label={`Editar grupo ${group.name}`}
          >
            <Edit2 className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={() => onDelete(group)}
            className='rounded-md p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500'
            title='Excluir grupo'
            aria-label={`Excluir grupo ${group.name}`}
          >
            <Trash2 className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={toggleExpand}
            className='ml-0.5 rounded-md p-2 text-gray-500 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            title={isExpanded ? 'Recolher grupo' : 'Ver permissões'}
            aria-label={
              isExpanded ? `Recolher grupo ${group.name}` : `Expandir grupo ${group.name}`
            }
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-gray-100 bg-gray-50/70 transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[5000px] border-t opacity-100' : 'max-h-0 border-t-0 opacity-0'
        }`}
      >
        <div className='space-y-4 p-3 sm:p-4'>
          {Object.entries(groupedPermissions)
            .sort(([a], [b]) => {
              if (a === 'agent-studio') return -1
              if (b === 'agent-studio') return 1
              return getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'pt-BR')
            })
            .map(([category, perms]) => (
              <section key={category}>
                <div className='mb-2 flex items-center gap-2'>
                  <span className='h-3.5 w-0.5 rounded-full bg-blue-500' />
                  <h4 className='text-[11px] font-bold uppercase tracking-wide text-gray-600'>
                    {getCategoryLabel(category)}
                  </h4>
                  <span className='ml-auto text-[10px] font-medium text-gray-400'>
                    {perms.length} {perms.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>

                <div className='grid grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'>
                  {[...perms]
                    .sort((a, b) => {
                      const aMatches = matchingPermissionIds.has(String(a.id_feature)) ? 1 : 0
                      const bMatches = matchingPermissionIds.has(String(b.id_feature)) ? 1 : 0
                      return bMatches - aMatches || a.title.localeCompare(b.title, 'pt-BR')
                    })
                    .map((permission) => {
                      const risk = getPermissionRisk(permission.name)
                      const isMatch = matchingPermissionIds.has(String(permission.id_feature))

                      return (
                        <div
                          key={permission.id_feature}
                          className={`flex min-h-[34px] items-center gap-2 rounded-md border px-2.5 py-1.5 transition ${
                            isMatch
                              ? 'border-blue-300 bg-blue-50 shadow-sm'
                              : 'border-gray-200 bg-white hover:border-blue-200'
                          }`}
                          title={permission.name}
                        >
                          <CheckCircle className='h-3.5 w-3.5 flex-shrink-0 text-emerald-500' />
                          <span className='min-w-0 flex-1 truncate text-xs font-medium text-gray-700'>
                            {permission.title}
                          </span>
                          {risk !== 'standard' && (
                            <span
                              className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                                risk === 'critical'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {risk === 'critical' ? 'Crítica' : 'Sensível'}
                            </span>
                          )}
                        </div>
                      )
                    })}
                </div>
              </section>
            ))}

          {resolvedPermissions.length === 0 && (
            <p className='py-3 text-center text-xs text-gray-500'>
              Este grupo não possui permissões reconhecidas.
            </p>
          )}
        </div>
      </div>
    </article>
  )
}
